import { cva } from 'class-variance-authority'
import { m } from 'motion/react'
import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react'
import { cn } from '../lib/cn.ts'
import { MOTION, type MotionSafe } from '../lib/motion.ts'

const pressTransition = { duration: MOTION.duration.sm, ease: MOTION.ease.out }

export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'destructive'
export type ButtonSize = 'sm' | 'md'

// Dark operations chrome: square corners, hairline or oxide borders, Archivo
// labels, no shadows. Keyboard focus comes from the global :focus-visible rule
// in globals.css so every interactive element matches.
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-none border font-display font-medium leading-none transition-colors duration-150 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'border-signal-oxide bg-signal-oxide text-ink-primary hover:border-signal-oxide-bright hover:bg-signal-oxide-bright',
        secondary: 'border-border-hairline-strong bg-ground-surface text-ink-primary hover:bg-ground-hover',
        // DESIGN_LANGUAGE §5 "Quiet": transparent on the control border, meta ink until hovered.
        quiet: 'border-border-control bg-transparent text-ink-meta hover:text-ink-primary',
        destructive: 'border-transparent bg-transparent text-signal-fail hover:border-signal-fail'
      },
      size: {
        sm: 'px-3 py-2 text-[11px]',
        md: 'px-3.5 py-2.5 text-xs'
      }
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md'
    }
  }
)

type ButtonOwnProps = { variant?: ButtonVariant; size?: ButtonSize }

type ButtonAsButtonProps = ButtonOwnProps & { as?: 'button' } & MotionSafe<ButtonHTMLAttributes<HTMLButtonElement>>
type ButtonAsAnchorProps = ButtonOwnProps & { as: 'a' } & MotionSafe<AnchorHTMLAttributes<HTMLAnchorElement>>

export type ButtonProps = ButtonAsButtonProps | ButtonAsAnchorProps

export function Button(props: ButtonProps) {
  const { variant = 'primary', size = 'md', className, as, ...rest } = props
  const classes = cn(buttonVariants({ variant, size }), className)

  // Lift/press feedback is transform-only (y + scale) so it layers cleanly on top of the
  // existing Tailwind color transitions above without touching background/border timing.
  if (as === 'a') {
    return (
      <m.a
        className={classes}
        whileHover={{ y: -1 }}
        whileTap={{ y: 0, scale: 0.97 }}
        transition={pressTransition}
        {...(rest as MotionSafe<AnchorHTMLAttributes<HTMLAnchorElement>>)}
      />
    )
  }
  return (
    <m.button
      type="button"
      className={classes}
      whileHover={{ y: -1 }}
      whileTap={{ y: 0, scale: 0.97 }}
      transition={pressTransition}
      {...(rest as MotionSafe<ButtonHTMLAttributes<HTMLButtonElement>>)}
    />
  )
}
